import type { Lesson, Program } from '../types';

export type ProgramLessonTemplateProgram = Pick<
  Program,
  'id' | 'academySportId' | 'skillLevel' | 'ageGroupId' | 'description' | 'imageUrl'
>;

export type ProgramLessonTemplateFields = {
  programId: string | null;
  academySportId?: string;
  skillLevel: NonNullable<Lesson['skillLevel']>;
  ageGroupId?: string | null;
  description?: string;
  imageUrl?: string;
};

/**
 * Maps a development program to lesson form defaults when creating or switching
 * the linked program on owner lesson/camp forms.
 */
export function buildLessonDefaultsFromProgram(
  program: ProgramLessonTemplateProgram
): ProgramLessonTemplateFields {
  const skillLevel = program.skillLevel ?? 'any';
  const description = program.description?.trim() || undefined;
  const imageUrl = program.imageUrl?.trim() || undefined;

  return {
    programId: program.id,
    ...(program.academySportId ? { academySportId: program.academySportId } : {}),
    skillLevel,
    ageGroupId: program.ageGroupId ?? null,
    ...(description ? { description } : { description: '' }),
    ...(imageUrl ? { imageUrl } : { imageUrl: '' }),
  };
}

/**
 * Applies program template fields onto a camp instance row (bulk wizard / backend).
 * Always overwrites skill level; fills description and image when row values are empty.
 */
export function applyProgramTemplateToCampInstance<
  T extends Partial<ProgramLessonTemplateFields> & { skillLevel?: ProgramLessonTemplateFields['skillLevel'] },
>(row: T, program: ProgramLessonTemplateProgram): T {
  const template = buildLessonDefaultsFromProgram(program);
  const description = row.description?.trim();
  const imageUrl = row.imageUrl?.trim();
  return {
    ...row,
    programId: template.programId,
    skillLevel: template.skillLevel,
    ...(template.academySportId ? { academySportId: template.academySportId } : {}),
    ageGroupId: template.ageGroupId ?? row.ageGroupId,
    description: description ? row.description : template.description,
    imageUrl: imageUrl ? row.imageUrl : template.imageUrl,
  };
}

